import React, { useState, useRef, forwardRef, useImperativeHandle, useContext } from 'react';
import { Drawer, Form, Space, Button, Input, Spin, Modal, Popconfirm, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';
import { parse2Number, parseFen2Currency, parseFen2Yuan, parseYuan2Fen } from '@/utils/number';
import * as ReverseApi from '@/api/bill/reverse';
import * as RefundApi from '@/api/bill/refund';
// import { BizErrorCode } from '@/api/bizCode';
import RefundByPaymentTypeModal, { IRefundRef } from '@/components/RefundByPaymentTypeModal';

import usePortalCalculator from '@/components/RefoundCalculatorModal';
import { isShowCalculator } from '@/components/RefoundCalculatorModal/help';
import IntlContext from '@/components/Intl/IntlContext';

import { validateAmount, formatAmount } from '../util/helper';
import { OpenCrossRefundDrawerProps, CrossRefundDrawerProps } from '../interface';
import { MT_ZL, XC_ZL, DY_ZL } from '../config';
import commonStyles from '../common.module.scss';
import CrossRefundInfo from './info';
import styles from './index.module.scss';

const { TextArea } = Input;

const CrossRefund: React.FC<CrossRefundDrawerProps> = forwardRef((props, ref) => {
  const refundRef = useRef<IRefundRef>();

  const [crossRefundForm] = Form.useForm();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [amount, setAmount] = useState(0); // 可退金额
  const [bill, setBill] = useState(null);
  const [crossRefundAmount, setCrossRefundAmount] = useState('');
  const [crossRefundRemark, setCrossRefundRemark] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const intlCtx = useContext(IntlContext);

  // 渠道退款计算器
  const calculatorPortal = usePortalCalculator();

  // 获取可退金额
  const fetchCanRefundAmount = async (no) => {
    try {
      const res = await ReverseApi.fetchReverseAmount({
        no,
      });
      if (res && res.status) {
        setAmount(res.data);
      }
    } catch (err) {
      setAmount(0);
    }
  };

  const openDrawer = (params: OpenCrossRefundDrawerProps) => {
    setBill(params.bill);
    fetchCanRefundAmount(params.bill.no);
    setDrawerVisible(true);
  };

  useImperativeHandle(ref, () => ({
    openDrawer,
  }));

  // reset states after drawer closed
  const changeVisible = (visible) => {
    if (!visible) {
      setAmount(null);
      setBill(null);
      setCrossRefundAmount('');
      setCrossRefundRemark('');
    }
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
  };

  // 金额输入框失焦
  const handleAmountInput = (value) => {
    const v = formatAmount(value, {
      max: parse2Number(parseFen2Yuan(amount)),
    });
    crossRefundForm.setFieldsValue({
      crossRefundAmount: v,
    });
  };

  // 确认退款
  const confirmRefund = async (data) => {
    const refundAmount = parse2Number(parseYuan2Fen(data.crossRefundAmount)); // 退款金额

    try {
      setSubmitLoading(true);

      const params = {
        billItemNo: bill.no, // 退款账单号
        amount: refundAmount, // 退款金额
        remark: data.remark, // 备注
        resvNo: bill.resvNo,
      };
      const res = await RefundApi.confirmCrossRefund(params);

      if (res && res.status) {
        message.success(intl.get('bill_cross_refound_success'));
        closeDrawer();

        // callback parent component
        props.crossRefundCallback();
      } else {
        message.success(res.msg || intl.get('bill_cross_refound_fail'));
      }

      setSubmitLoading(false);
    } catch (err) {
      Modal.error({
        title: intl.get('bill_cross_refound_fail'),
        content: (
          <div style={{ padding: 20 }}>
            <p>{intl.get('bill_cross_refound_fail_reason')}</p>
            <p>{err?.msg || ''}</p>
            <p style={{ marginTop: 20 }}>{intl.get('bill_cross_refound_retry')}</p>
          </div>
        ),
        closable: true,
        okText: intl.get('app_confirm_confirm_btn'),
      });
      setSubmitLoading(false);
    }
  };

  // 提交退款
  const submit = async () => {
    if (amount === 0) {
      message.warning(intl.get('bill_cross_refound_can_not_for_zero'));
      return;
    }

    let values;
    try {
      values = await crossRefundForm.validateFields();
    } catch (err) {
      // TODO, parse err
      message.warning(intl.get('bill_cross_refound_input_refound_amount'));
    }

    // 退款二次确认
    refundRef.current?.showModal({
      dataSource: [
        {
          id: bill.no,
          subjectName: bill.subjectCodeName,
          paymentType: bill.paymentType,
          refundAmount: parse2Number(parseYuan2Fen(values.crossRefundAmount)),
          isAcrossNight: true,
        },
      ],
      onConfirm: async () => {
        await confirmRefund(values);
      },
    });
  };

  const drawerFooter = () => {
    // 美团直连、携程直连预付款退款二次确认
    const refundConfirm =
      bill?.paymentType === MT_ZL || bill?.paymentType === XC_ZL || bill?.paymentType === DY_ZL; // 直连预付款退款二次确认

    let channelName = '';
    if (bill?.paymentType === MT_ZL) {
      channelName = intl.get('app_meituan');
    }
    if (bill?.paymentType === XC_ZL) {
      channelName = intl.get('app_trip');
    }
    if (bill?.paymentType === DY_ZL) {
      channelName = intl.get('app_tiktok');
    }

    return (
      <Space size={16} style={{ marginLeft: 8 }}>
        {refundConfirm ? (
          <Popconfirm
            title={
              <div>
                <span>{intl.get('bill_cross_refound_confirm_refound_title', { channelName })}</span>
                <p style={{ color: 'red' }}>{intl.get('bill_cross_refound_alert')}</p>
              </div>
            }
            okText={intl.get('bill_cross_refound_confirm')}
            cancelText={intl.get('bill_cross_refound_click_wrong')}
            okButtonProps={{
              disabled:
                crossRefundAmount === '' || +crossRefundAmount <= 0 || crossRefundRemark === '',
              loading: submitLoading,
            }}
            onConfirm={() => submit()}
          >
            <Button
              type="primary"
              danger
              disabled={
                crossRefundAmount === '' || +crossRefundAmount <= 0 || crossRefundRemark === ''
              }
            >
              {intl.get('bill_cross_refound_title')}
            </Button>
          </Popconfirm>
        ) : (
          <Button
            type="primary"
            danger
            disabled={
              crossRefundAmount === '' || +crossRefundAmount <= 0 || crossRefundRemark === ''
            }
            loading={submitLoading}
            onClick={() => submit()}
          >
            {intl.get('bill_cross_refound_title')}
          </Button>
        )}
        <Button onClick={() => closeDrawer()}>{intl.get('app_confirm_cancel_btn')}</Button>
      </Space>
    );
  };

  // 计算退款金额
  const calculatorRefoundAmount = () => {
    const params = {
      channelNo: bill?.channelNo, // 渠道订单号
      payChanel: bill?.paymentType, // 支付渠道
      maxRefoundAmount: amount, // 最大可退金额
    };
    calculatorPortal.showDrawer({
      title: intl.get('bill_cross_refound_calculator'),
      cancelText: intl.get('app_confirm_close_btn'),
      ...params,
      onConfirm: async (refundAmount: any) => {
        crossRefundForm.setFieldsValue({ crossRefundAmount: refundAmount });
        calculatorPortal.closeDrawer();
      },
      onCancel: () => {
        calculatorPortal.closeDrawer();
      },
    });
  };

  return (
    <Drawer
      className={styles.crossRefundDrawer}
      width="50%"
      title={intl.get('bill_cross_refound_title')}
      open={drawerVisible}
      maskClosable={false}
      closable={false}
      destroyOnClose
      afterOpenChange={(visible) => changeVisible(visible)}
      extra={
        <Button
          style={{ border: 0 }}
          shape="circle"
          onClick={() => closeDrawer()}
          icon={<CloseOutlined />}
        />
      }
      footer={drawerFooter()}
    >
      <RefundByPaymentTypeModal ref={refundRef} />
      <CrossRefundInfo info={bill} />
      <Spin spinning={submitLoading}>
        <dl className={styles['crossRefund-amount']}>
          <dt className={commonStyles['bill-block-title']}>
            {intl.get('bill_cross_refound_infos')}
          </dt>
          <dd>
            <Form form={crossRefundForm} preserve={false} className={styles['crossRefund-form']}>
              <Form.Item label={intl.get('bill_cross_refound_avalible_amount')} name="amount">
                {parseFen2Currency(amount)}
              </Form.Item>
              <Form.Item label={intl.get('bill_cross_refound_amount')}>
                <Form.Item
                  name="crossRefundAmount"
                  initialValue={crossRefundAmount}
                  rules={[
                    {
                      validator: (_, value) => {
                        return validateAmount(value, {
                          required: true,
                          requiredMsg: intl.get('bill_cross_refound_input_refound_amount'),
                        });
                      },
                    },
                  ]}
                >
                  <Input
                    allowClear
                    prefix={intlCtx.TenantBaseConfig.CurrencySymbol}
                    placeholder={intl.get('bill_cross_refound_input_refound_amount')}
                    onChange={(e) => {
                      setCrossRefundAmount(e.target.value);
                    }}
                    onBlur={(e) => handleAmountInput(e.target.value)}
                  />
                </Form.Item>
                {/* 退款渠道计算器入口 */}
                {isShowCalculator(bill?.subjectCode) && (
                  <span className={styles.refoundCalculator} onClick={calculatorRefoundAmount} />
                )}
              </Form.Item>
              <Form.Item
                label={intl.get('bill_remark')}
                name="remark"
                initialValue={crossRefundRemark}
                rules={[
                  { required: true, message: intl.get('bill_cross_refound_account_remarks') },
                ]}
              >
                <TextArea
                  showCount
                  rows={4}
                  maxLength={10000}
                  placeholder={intl.get('bill_cross_refound_remark_label')}
                  onChange={(e) => {
                    setCrossRefundRemark(e.target.value);
                  }}
                />
              </Form.Item>
            </Form>
          </dd>
        </dl>
      </Spin>
      <div className={styles.crossRefundHint}>{intl.get('bill_cross_refound_make_time_info')}</div>
    </Drawer>
  );
});

export default CrossRefund;
